import React, { useEffect, useRef, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { Navigate } from 'react-router';
import { getRouterPathname } from '../constants/appBasePath';
import { llcUrl } from '../constants/externalLinks';
import { useLlcApi } from '../Api';
import { useLoginProfileContext } from '../contexts/LoginProfileContext';

interface Props {
  children: React.ReactNode;
}

const loadingEl = (
  <div className="flex min-h-screen items-center justify-center bg-surface-secondary">
    <p className="text-sm text-on-surface-tertiary">Loading...</p>
  </div>
);

export default function ProtectedRoute({ children }: Props) {
  const { isAuthenticated, isLoading, user, getAccessTokenSilently } =
    useAuth0();
  const llcApi = useLlcApi();
  const { loading: profileLoading } = useLoginProfileContext();
  const hasStartedRefresh = useRef(false);
  const hasLinkedCid = useRef(false);
  const [refreshed, setRefreshed] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    if (
      isLoading ||
      !isAuthenticated ||
      !user?.email_verified ||
      profileLoading ||
      hasLinkedCid.current
    ) {
      return;
    }

    const cid = localStorage.getItem('trakdesk_cid');
    if (!cid) {
      hasLinkedCid.current = true;
      return;
    }

    hasLinkedCid.current = true;
    llcApi
      .post('/api/LoginProfile/trackdesk-cid', { cid })
      .then(() => localStorage.removeItem('trakdesk_cid'))
      .catch((err) => {
        console.error('Failed to link Trackdesk CID:', err);
        hasLinkedCid.current = false;
      });
  }, [
    isLoading,
    isAuthenticated,
    user?.email_verified,
    profileLoading,
    llcApi,
  ]);

  useEffect(() => {
    if (
      !isLoading &&
      isAuthenticated &&
      !user?.email_verified &&
      !hasStartedRefresh.current
    ) {
      hasStartedRefresh.current = true;
      setRefreshing(true);
      getAccessTokenSilently({ cacheMode: 'off' })
        .catch(() => {})
        .finally(() => {
          setRefreshing(false);
          setRefreshed(true);
        });
    }
  }, [
    isLoading,
    isAuthenticated,
    user?.email_verified,
    getAccessTokenSilently,
  ]);

  useEffect(() => {
    if (
      !isLoading &&
      !refreshing &&
      isAuthenticated &&
      refreshed &&
      !user?.email_verified
    ) {
      const returnTo = encodeURIComponent(window.location.href);
      window.location.replace(llcUrl(`/verify-email?returnTo=${returnTo}`));
    }
  }, [isLoading, refreshing, isAuthenticated, refreshed, user?.email_verified]);

  if (isLoading || refreshing) return loadingEl;

  if (!isAuthenticated) {
    const returnTo = getRouterPathname() + window.location.search;
    return <Navigate to="/signup" state={{ returnTo }} replace />;
  }

  if (!user?.email_verified) {
    return loadingEl;
  }

  return <>{children}</>;
}
